import { assert, isSome, Maybe } from '@crocodille/common';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef } from 'react';
import {
  IntersectionObserverEntryWithPayload,
  IntersectionObserverProvider,
} from '../../../hooks/use-intersection-observer';
import { useThrottledCallback } from '../../../hooks/use-throttled-callback';
import * as routing from '../../../routing';

import styles from './product-list.module.scss';

const Navigation: React.FC<
  React.PropsWithChildren<Record<string, unknown>>
> = ({ children }) => {
  return <div className={styles.navigation}>{children}</div>;
};

const OBSERVER_THRESHOLDS = [
  0.025, 0.05, 0.1, 0.25, 0.5, 0.75, 0.9, 0.95, 0.975, 1.0,
];

/**
 * Main product list. Container for a list of branch taxons containing products.
 */
const ProductListElement: React.FC<
  React.PropsWithChildren<{
    readonly taxonSlug: string;
    readonly disableRouteReplace?: boolean;
    onProductsShown(productIds: readonly number[]): void;
  }>
> = ({ children, taxonSlug, onProductsShown, disableRouteReplace }) => {
  const { replace, query } = useRouter();

  const productSlugRef = useRef(query.product as string | undefined);
  productSlugRef.current = query.product as string | undefined;

  const taxonSlugRef = useRef(taxonSlug);
  taxonSlugRef.current = taxonSlug;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    document
      .querySelector(`#${taxonSlug} .scroll-anchor`)
      ?.scrollIntoView({ block: 'start', behavior: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const intersectionSizesRef = useRef<Record<string, number>>({});

  const updateSlug = useThrottledCallback(() => {
    let targetTaxon: Maybe<[string, number]> = null;

    for (const taxonIntersectionSize of Object.entries(
      intersectionSizesRef.current,
    )) {
      const [_taxon, intersectionSize] = taxonIntersectionSize;

      if (!targetTaxon) {
        targetTaxon = taxonIntersectionSize;
      }

      const [_targetTaxon, targetIntersectionSize] = targetTaxon;

      if (intersectionSize > targetIntersectionSize) {
        targetTaxon = taxonIntersectionSize;
      }
    }

    if (
      !disableRouteReplace &&
      targetTaxon &&
      targetTaxon[0] !== taxonSlugRef.current
    ) {
      replace(
        routing.products({
          taxon: targetTaxon[0],
          product: productSlugRef.current,
        }),
        undefined,
        {
          shallow: true,
        },
      );
    }
  }, 150);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntryWithPayload[]) => {
      const entriesWithPayload = entries.filter(
        (
          entry,
        ): entry is IntersectionObserverEntryWithPayload & {
          payload: { type: string; [field: string]: unknown };
        } =>
          isSome(entry.payload) &&
          typeof entry.payload === 'object' &&
          'type' in entry.payload,
      );

      const productsShown = [];

      for (const entry of entriesWithPayload) {
        if (entry.payload.type === 'products-taxon') {
          assert(
            'slug' in entry.payload && typeof entry.payload.slug === 'string',
          );

          const taxon = entry.payload.slug;

          if (entry.isIntersecting) {
            intersectionSizesRef.current[taxon] = entry.intersectionRect.height;
          } else {
            delete intersectionSizesRef.current[taxon];
          }
        } else if (
          entry.payload.type === 'product-card' &&
          entry.isIntersecting &&
          entry.intersectionRatio > 0.5
        ) {
          assert('id' in entry.payload && typeof entry.payload.id === 'number');

          const productId = entry.payload.id;

          productsShown.push(productId);
        }
      }

      if (productsShown.length > 0) {
        onProductsShown(productsShown);
      }

      updateSlug();
    },
    [updateSlug, onProductsShown],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className={styles.container}>
      <IntersectionObserverProvider
        threshold={OBSERVER_THRESHOLDS}
        callback={observerCallback}
        // 120px is height of header and taxon navigation combined
        rootMargin="150px 0px 20px 0px"
      >
        {children}
      </IntersectionObserverProvider>
    </div>
  );
};

const ProductList = Object.assign(ProductListElement, { Navigation });

export default ProductList;
